/**
 * Caricamento diretto su S3: il backend controlla i limiti del piano e
 * restituisce un permesso per file (URL firmato dal motore); il browser
 * carica ogni file su S3, senza passare da Nginx e dal backend, e poi manda
 * a /api/anonymize solo le chiavi.
 */

const IN_PARALLELO = 3;

function errore(messaggio, status) {
  const e = new Error(messaggio);
  e.isFriendly = true;
  e.status = status;
  return e;
}

async function erroreDalBackend(r) {
  let dettaglio = null;
  try { dettaglio = (await r.json())?.detail; } catch (_) { /* corpo non JSON */ }
  if (typeof dettaglio === "string" && dettaglio) return errore(dettaglio, r.status);
  return errore("Il caricamento dei file non è riuscito. Riprova fra qualche istante.", r.status);
}

/** Un file su S3 con un POST firmato; onByte riceve i byte gia' inviati. */
export function inviaConXhr({ url, campi }, file, onByte) {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    for (const [k, v] of Object.entries(campi || {})) fd.append(k, v);
    fd.append("file", file); // per ultimo: S3 ignora i campi che vengono dopo il file
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onByte(Math.min(file.size, (e.loaded / e.total) * file.size));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) { onByte(file.size); resolve(); return; }
      // S3 risponde in XML: EntityTooLarge se il file supera il limite firmato.
      const troppoGrande = /EntityTooLarge/.test(xhr.responseText || "");
      reject(errore(troppoGrande
        ? `"${file.name}" supera il limite per file del tuo piano.`
        : "Il caricamento dei file non è riuscito. Riprova fra qualche istante.", xhr.status));
    };
    xhr.onerror = () => reject(errore("Connessione interrotta durante il caricamento. Verifica la connessione e riprova.", 0));
    xhr.send(fd);
  });
}

/**
 * Carica i file e restituisce le voci per /api/anonymize: chiave, nome,
 * dimensione. onAvanzamento riceve la frazione caricata (0..1).
 */
export async function caricaSuS3(files, { onAvanzamento, fetchFn = fetch, inviaFn = inviaConXhr } = {}) {
  const lista = Array.from(files);
  const r = await fetchFn("/api/caricamenti", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ file: lista.map((f) => ({ dimensione: f.size })) }),
  });
  if (!r.ok) throw await erroreDalBackend(r);
  const { caricamenti } = await r.json();
  if (!Array.isArray(caricamenti) || caricamenti.length !== lista.length) {
    throw errore("Il caricamento dei file non è riuscito. Riprova fra qualche istante.", 502);
  }

  const totale = lista.reduce((s, f) => s + f.size, 0) || 1;
  const inviati = lista.map(() => 0);
  const avvisa = () => onAvanzamento && onAvanzamento(inviati.reduce((s, n) => s + n, 0) / totale);
  let prossimo = 0;
  const lavora = async () => {
    while (prossimo < lista.length) {
      const i = prossimo++;
      await inviaFn(caricamenti[i], lista[i], (byte) => { inviati[i] = byte; avvisa(); });
    }
  };
  await Promise.all(Array.from({ length: Math.min(IN_PARALLELO, lista.length) }, lavora));
  return lista.map((f, i) => ({ chiave: caricamenti[i].chiave, nome: f.name, dimensione: f.size }));
}
